import React, { useCallback, useEffect } from 'react';
import { useLocalSearchParams, useNavigation, useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { useA11ySelector } from '@sg/a11y-helpers';
import { DisplayText, Tabs, theme, useResponsive } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import {
  AccountScreenContainer,
  AccountScreenSection,
  FloatingButtons,
  SignedOutView,
} from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import { useCart } from '../../hooks';
import { OrdersHeader } from './components';
import { useOrdersTabs } from './hooks';
import { DigitalOrders, GiftCardOrders, InStoreOrders, Orders } from './tabs';

// ─────────────────────────────────────────────────────────────────────────────

export const OrdersScreen = () => {
  const isLoggedIn = useIsLoggedIn();

  // ─────────────────────────────────────────────────────────────────────

  if (!isLoggedIn) return <SignedOutView />;

  return <OrdersScreenLoggedIn />;
};

// ─────────────────────────────────────────────────────────────────────────────

export const OrdersScreenLoggedIn = () => {
  const isInStoreOrdersEnabled = useFeatureFlag('CELS-4174-in-store-loyalty-orders-enabled');
  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');

  // ─── Navigation ──────────────────────────────────────────────────────

  const navigation = useNavigation();
  const router = useRouter();

  const routeParams = useLocalSearchParams<{ tabId?: OrderTabId }>();
  const tabIdParam = routeParams.tabId;

  const { match, currentBreakpoint } = useResponsive();
  const { formatMessage } = useIntl();

  const containerA11ySelector = useA11ySelector('account.orders.container');

  // ─── Tabs ────────────────────────────────────────────────────────────

  const { activeTabItemId, setActiveTabItemId, firstTab, secondTab, tabs } = useOrdersTabs({
    tabIdParam,
    isInStoreOrdersEnabled,
  });

  // ─── Cart ────────────────────────────────────────────────────────────

  const { lineItemsCount } = useCart();

  const navigateToBag = useCallback(() => {
    router.navigate('/bag');
  }, [router]);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const onTabChange = useCallback(
    (tabId: string) => {
      setActiveTabItemId(tabId);

      router.setParams({ tabId });
    },
    [router, setActiveTabItemId],
  );

  // ─── Styles ──────────────────────────────────────────────────────────

  const tabsBarStyle = [
    styles.screenInnerContainer,
    match([styles.tabsContainerXS, styles.tabsContainerSM]),
  ];

  const removeContainerSpacing = isInStoreOrdersEnabled ? currentBreakpoint.isXS : false;

  // ─── Effects ─────────────────────────────────────────────────────────

  const header = useCallback(() => {
    if (!currentBreakpoint.isXS) return null;

    return <OrdersHeader isReorderEnabled={firstTab.id === activeTabItemId} />;
  }, [currentBreakpoint.isXS, firstTab.id, activeTabItemId]);

  const setOptions = navigation.setOptions;

  useEffect(() => {
    setOptions({ header, headerShown: true });
  }, [setOptions, header]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <AccountScreenContainer
        screen="orders"
        removeSpacing={removeContainerSpacing}
        {...containerA11ySelector.props}
      >
        <View>
          <DisplayText sizeMatch={['32']} style={match([styles.pageTitleXS, null])} role="heading">
            {formatMessage(messages.screenTitle)}
          </DisplayText>

          <Tabs.Bar
            style={tabsBarStyle}
            palette="primary"
            tabs={tabs}
            activeTabId={activeTabItemId}
            onChange={onTabChange}
          />
        </View>

        <View style={styles.screenInnerContainer}>
          <AccountScreenSection style={styles.content}>
            <Tabs.Panel
              isActive={activeTabItemId === firstTab.id}
              tabId={firstTab.id}
              panelId={firstTab.panelId}
              style={activeTabItemId === firstTab.id ? styles.content : null}
            >
              {isInStoreOrdersEnabled ? <DigitalOrders /> : <Orders />}
            </Tabs.Panel>

            <Tabs.Panel
              isActive={activeTabItemId === secondTab.id}
              tabId={secondTab.id}
              panelId={secondTab.panelId}
              style={activeTabItemId === secondTab.id ? styles.content : null}
            >
              {isInStoreOrdersEnabled ? <InStoreOrders /> : <GiftCardOrders />}
            </Tabs.Panel>
          </AccountScreenSection>
        </View>
      </AccountScreenContainer>

      {lineItemsCount > 0 && isInStoreOrdersEnabled && !isNavbarV2Enabled ? (
        <FloatingButtons.Container anchor="right">
          <FloatingButtons.Bag showMessage={false} onPress={navigateToBag} />
        </FloatingButtons.Container>
      ) : null}
    </>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

export const messages = defineMessages({
  screenTitle: {
    defaultMessage: 'Orders',
    description: 'Orders Screen | Screen title',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pageTitleXS: {
    paddingTop: theme.spacing['1'],
    paddingHorizontal: theme.spacing['4'],
    paddingBottom: theme.spacing['4'],
  },
  content: {
    flex: 1,
    maxWidth: '100%',
  },
  screenInnerContainer: {
    flex: 1,
    width: '100%',
    maxWidth: 744,
  },
  tabsContainerXS: {
    marginBottom: theme.spacing['4'],
  },
  tabsContainerSM: {
    marginVertical: theme.spacing['6'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderTabId = 'orders' | 'gift-cards' | 'digital-orders' | 'in-store-orders';
